import { useEffect, useState, useCallback } from "react";
import { PackageSearch, RefreshCw, Mail, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import { adminGetBackorders, adminUpdateBackorder } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUS_VARIANT = {
  new: "default",
  contacted: "secondary",
  closed: "outline",
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

export const BackordersManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      setData(await adminGetBackorders(params));
    } catch {
      toast.error("Failed to load backorder requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await adminUpdateBackorder(id, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const counts = data?.counts || { new: 0, contacted: 0, closed: 0 };
  const rows = data?.requests || [];

  return (
    <div className="space-y-5" data-testid="backorders-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl">Backorder Requests</h2>
          <p className="text-sm text-muted-foreground">Leads captured from out-of-stock product pages.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]" data-testid="backorders-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} aria-label="Refresh" data-testid="backorders-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["New", counts.new], ["Contacted", counts.contacted], ["Closed", counts.closed]].map(([label, val]) => (
          <div key={label} className="rounded-xl border border-border bg-card p-4" data-testid={`backorders-count-${label.toLowerCase()}`}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="mt-1 font-mono text-2xl font-semibold">{val}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-3 flex items-center gap-2">
          <PackageSearch className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Requests</h3>
        </div>
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="w-16 text-right">Qty</TableHead>
                  <TableHead>Email sent</TableHead>
                  <TableHead className="w-[150px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length ? rows.map((r) => (
                  <TableRow key={r.id} data-testid="backorder-row">
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <p className="truncate text-sm font-medium">{r.title}</p>
                      {r.variant_title && r.variant_title !== "Default Title" && (
                        <p className="truncate font-mono text-[11px] text-muted-foreground">{r.variant_title}</p>
                      )}
                      {r.message && <p className="mt-0.5 truncate text-xs text-muted-foreground" title={r.message}>“{r.message}”</p>}
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="font-medium">{r.name || "—"}</p>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                        <Mail className="h-3 w-3" /> {r.email}
                      </a>
                      {r.phone && <span className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" /> {r.phone}</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono">{r.quantity}</TableCell>
                    <TableCell>
                      {r.email_sent
                        ? <Badge variant="secondary" className="gap-1"><Check className="h-3 w-3" /> Sent</Badge>
                        : <Badge variant="outline" className="text-muted-foreground">No</Badge>}
                    </TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`backorder-status-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      No backorder requests yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};
